import { useEffect } from 'react';
import { hasFinePointer, shouldAnimate } from '../utils/motion';

/**
 * Adds a ripple to every .btn-cta / .btn-outline inside rootRef and a subtle
 * magnetic pull to elements marked with [data-magnetic] (mouse only).
 * Uses event delegation, so links keep their own onClick handlers.
 */
export default function useButtonEffects(rootRef) {
  useEffect(() => {
    const root = rootRef.current;
    if (!root || !shouldAnimate()) return undefined;

    const fine = hasFinePointer();
    let activeMagnet = null;

    const handlePointerDown = (event) => {
      const button = event.target.closest('.btn-cta, .btn-outline, .home-btn-ghost');
      if (!button || !root.contains(button)) return;

      const rect = button.getBoundingClientRect();
      const size = Math.max(rect.width, rect.height);
      const ripple = document.createElement('span');
      ripple.className = 'btn-ripple';
      ripple.style.width = `${size}px`;
      ripple.style.height = `${size}px`;
      ripple.style.left = `${event.clientX - rect.left - size / 2}px`;
      ripple.style.top = `${event.clientY - rect.top - size / 2}px`;
      button.appendChild(ripple);
      window.setTimeout(() => ripple.remove(), 650);
    };

    const resetMagnet = () => {
      if (activeMagnet) activeMagnet.style.transform = '';
      activeMagnet = null;
    };

    const handlePointerMove = (event) => {
      const magnet = event.target.closest('[data-magnetic]');
      if (magnet !== activeMagnet) resetMagnet();
      if (!magnet || !root.contains(magnet)) return;

      activeMagnet = magnet;
      const rect = magnet.getBoundingClientRect();
      const x = (event.clientX - rect.left - rect.width / 2) * 0.18;
      const y = (event.clientY - rect.top - rect.height / 2) * 0.3;
      magnet.style.transform = `translate(${x}px, ${y}px)`;
    };

    root.addEventListener('pointerdown', handlePointerDown);
    if (fine) {
      root.addEventListener('pointermove', handlePointerMove);
      root.addEventListener('pointerleave', resetMagnet);
    }

    return () => {
      root.removeEventListener('pointerdown', handlePointerDown);
      root.removeEventListener('pointermove', handlePointerMove);
      root.removeEventListener('pointerleave', resetMagnet);
      resetMagnet();
    };
  }, [rootRef]);
}
